import { Event } from "../../types/eventTypes";
import { Box, Button, Typography } from "@mui/material";
import ReactCalendar from "react-calendar";
import { useRouter } from "next/router";
import { format, isSameDay } from "date-fns";
import { dcContent } from "../../../services/content";
import enLocale from "date-fns/locale/en-GB";
import { useContext, useEffect, useState } from "react";
import { useScreenSize } from "../../hooks/useScreenSize";
import { LocalizationContext } from "../../lib/context";

const EventCardSmall = ({ event }: { event: Event }) => {
  if (!event) {
    return <></>;
  }
  const { eventsPage } = useContext(LocalizationContext).localeStrings;
  const locale = useRouter().locale;
  const [dateLocale, setDateLocale] = useState(enLocale);
  useEffect(() => {
    const importLocaleFile = async (dateFnLocaleCode: string) => {
      const localeToSet = await import(
        `date-fns/locale/${dateFnLocaleCode}/index.js`
      );
      setDateLocale(localeToSet.default);
    };
    if (locale !== dateLocale.code) {
      const dateFnLocaleCode = dcContent.routerLocaleToDateFn.filter(
        (l) => l.locale === locale
      )[0].dateFnLocale;
      importLocaleFile(dateFnLocaleCode).catch();
    }
  }, [locale, dateLocale]);

  return (
    <Box
      my={2}
      sx={{
        display: "flex",
        flexDirection: "row",
        width: "100%",
        border: "1px solid",
        borderColor: "primary.main",
        borderRadius: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          m: 1,
          width: "70%",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            fontSize: 14,
            color: "white",
            backgroundColor: "primary.light",
            borderRadius: 2,
            py: 1,
          }}
        >
          <Box>{format(event.date, "PP", { locale: dateLocale })}</Box>
          <Box>{format(event.date, "EEEE", { locale: dateLocale })}</Box>
          <Box>{format(event.date, "p", { locale: dateLocale })}</Box>
        </Box>
        <Box>
          <Button
            sx={{ mt: 1, width: "100%" }}
            variant="outlined"
            onClick={() => {
              window.open(event.eventUrl, "_blank", "noreferrer");
            }}
          >
            {eventsPage.buttonEventPage}
          </Button>
          {event.ticketUrl && (
            <Button
              sx={{ mt: 1, width: "100%" }}
              variant="outlined"
              onClick={() => {
                window.open(event.ticketUrl, "_blank", "noreferrer");
              }}
            >
              {eventsPage.buttonTicketPage}
            </Button>
          )}
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
          p: 2,
        }}
      >
        <Typography variant="body1" fontSize={16} fontWeight="bold">
          {event.name}
        </Typography>
      </Box>
    </Box>
  );
};

export const EventCardLarge = ({ event }: { event: Event }) => {
  if (!event) {
    return <></>;
  }
  const { eventsPage } = useContext(LocalizationContext).localeStrings;
  const mdUp = useScreenSize("md", "up");
  const eventsXsUp = useScreenSize(424, "up");
  const locale = useRouter().locale;
  const [dateLocale, setDateLocale] = useState(enLocale);
  useEffect(() => {
    const importLocaleFile = async (dateFnLocaleCode: string) => {
      const localeToSet = await import(
        `date-fns/locale/${dateFnLocaleCode}/index.js`
      );
      setDateLocale(localeToSet.default);
    };
    if (locale !== dateLocale.code) {
      const dateFnLocaleCode = dcContent.routerLocaleToDateFn.filter(
        (l) => l.locale === locale
      )[0].dateFnLocale;
      importLocaleFile(dateFnLocaleCode).catch();
    }
  }, [locale, dateLocale]);

  return (
    <Box
      pt={1}
      my={2}
      sx={{
        display: "flex",
        flexDirection: "row",
        justifyContent: "center",
        alignItems: "center",
        width: mdUp ? "80%" : "100%",
        border: "1px solid",
        borderColor: "primary.main",
        borderRadius: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          width: eventsXsUp ? "30%" : "40%",
          color: "white",
          backgroundColor: "primary.light",
          borderRadius: 2,
          m: 2,
          py: 1,
          fontSize: mdUp ? 18 : eventsXsUp ? 16 : 14,
        }}
      >
        <Box>{format(event.date, "PP", { locale: dateLocale })}</Box>
        <Box>{format(event.date, "EEEE", { locale: dateLocale })}</Box>
        <Box>{format(event.date, "p", { locale: dateLocale })}</Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          width: "70%",
          p: mdUp ? 2 : 1,
        }}
      >
        <Box>
          <Typography variant="body1" fontSize={18} fontWeight="bold">
            {event.name}
          </Typography>
        </Box>
        <Box sx={{ my: 1 }}>
          <Button
            sx={{ px: 2, mr: 1, fontSize: 14 }}
            variant="outlined"
            onClick={() => {
              window.open(event.eventUrl, "_blank", "noreferrer");
            }}
          >
            {eventsPage.buttonEventPage}
          </Button>
          {event.ticketUrl && (
            <Button
              sx={{
                mt: eventsXsUp ? 0 : 1,
                px: 2,
                fontSize: 14,
              }}
              variant="outlined"
              onClick={() => {
                window.open(event.ticketUrl, "_blank", "noreferrer");
              }}
            >
              {eventsPage.buttonTicketPage}
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export const EventCalendar = ({ events }: { events: Event[] }) => {
  const { eventsPage } = useContext(LocalizationContext).localeStrings;
  const lg = useScreenSize("lg", "up");
  const router = useRouter();
  const [selectedDay, setSelectedDay] = useState<Date>(new Date());
  const [eventOnSelectedDay, setEventOnSelectedDay] = useState<Event[]>(
    events.filter((event) => isSameDay(event.date, Date.now()))
  );

  const highlightedDates = events.map((event) => event.date);
  const tileClassName = ({ date, view }: { date: Date; view: string }) => {
    if (
      view === "month" &&
      highlightedDates.find((dDate: number | Date) => isSameDay(dDate, date))
    ) {
      return "highlight";
    }
    return "";
  };

  const changeDay = (value: Date) => {
    const eventsOnThisDay = events.filter((event) =>
      isSameDay(event.date, value)
    );
    setSelectedDay(value);
    setEventOnSelectedDay(eventsOnThisDay);
  };

  console.log(eventOnSelectedDay);

  return (
    <Box
      sx={{
        width: lg ? "80%" : "70%",
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "top",
        gap: lg ? 5 : 2,
        mt: 5,
      }}
    >
      <Box
        sx={{
          width: "55%",
        }}
      >
        {eventOnSelectedDay.length > 0 ? (
          eventOnSelectedDay.map((event) => (
            <EventCardSmall key={event.date} event={event} />
          ))
        ) : (
          <Typography
            variant="body1"
            fontSize={18}
            fontWeight="bold"
            color="error.main"
          >
            {isSameDay(selectedDay, new Date())
              ? eventsPage.noEventsToday
              : eventsPage.noEventsSelectedDay}
          </Typography>
        )}
      </Box>
      <Box
        sx={{
          width: "45%",
        }}
      >
        <ReactCalendar
          onClickDay={changeDay}
          locale={router.locale}
          minDetail="decade"
          minDate={new Date("2023-05-01")}
          tileClassName={tileClassName}
        />
      </Box>
    </Box>
  );
};
